import { computed } from 'vue'
import { enumValue } from '@siclop/kstone-vue'
import { ContractType, useContractTypeLabels } from '../enums/ContractType'

export type ContractTagType = 'success' | 'warning' | 'danger' | 'info' | 'primary'

const TAG_TYPES: Record<ContractType, ContractTagType> = {
  [ContractType.CDI]: 'success',
  [ContractType.CDD]: 'warning',
  [ContractType.Temporary]: 'info',
  [ContractType.Intern]: 'primary',
  [ContractType.Apprentice]: 'primary',
  [ContractType.Freelance]: 'danger',
  [ContractType.PartTime]: 'info'
}

const COLORS: Record<ContractType, string> = {
  [ContractType.CDI]: '#67C23A',
  [ContractType.CDD]: '#E6A23C',
  [ContractType.Temporary]: '#909399',
  [ContractType.Intern]: '#409EFF',
  [ContractType.Apprentice]: '#409EFF',
  [ContractType.Freelance]: '#F56C6C',
  [ContractType.PartTime]: '#909399'
}

/** Ordre d'affichage des types de contrat dans les listes de choix. */
const ORDER: ContractType[] = [
  ContractType.CDI, ContractType.CDD, ContractType.Temporary, ContractType.Intern,
  ContractType.Apprentice, ContractType.Freelance, ContractType.PartTime
]

/**
 * Affichage du type de contrat d'un salarié.
 *
 * L'API envoie le nom de l'énumération (`"CDI"`, `"CDD"`, `"PartTime"`…) ; `defineEnum` ne reconnaît que les
 * clés en PascalCase dérivées (`"Cdi"`), d'où « Type CDI » ou un mauvais libellé. On ramène d'abord la valeur
 * au nombre par `enumValue(ContractType, …)`.
 */
export function useContractTypeDisplay() {
  const labels = useContractTypeLabels()

  function toContractType(value: unknown): ContractType | undefined {
    const n = enumValue(ContractType, value)
    return n !== undefined && n in TAG_TYPES ? (n as ContractType) : undefined
  }

  function contractLabel(value: unknown): string {
    const n = toContractType(value)
    return n === undefined ? '-' : labels.getLabel(n)
  }

  function contractTagType(value: unknown): ContractTagType {
    const n = toContractType(value)
    return n === undefined ? 'info' : TAG_TYPES[n]
  }

  function contractColor(value: unknown): string {
    const n = toContractType(value)
    return n === undefined ? '#909399' : COLORS[n]
  }

  const contractTypeOptions = computed(() => ORDER.map(value => ({ value, label: labels.getLabel(value) })))

  return { toContractType, contractLabel, contractTagType, contractColor, contractTypeOptions }
}
